import { useState, useRef, useImperativeHandle, forwardRef, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  Button,
  IconButton,
} from '@mui/material';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import CloseIcon from '@mui/icons-material/Close';
import { filterUndefined } from '../../../utils/FilterUndefinedObject';
import './CardDetailContainer.scss';
import LiveChatService from '../../../services/LiveChatService';

const defaultProps = {
  open: false,
  selectedIndex: -1,
  onClose: () => { },
  className: '',
  onPageChange: () => { },
};

const CardDetailContainer = forwardRef(({
  ...props
}, ref) => {
  const {
    open,
    selectedIndex,
    onClose,
    dataList,
    createCardFunction,
    className,
    onPageChange,
    ...rest
  } = { ...defaultProps, ...filterUndefined(props) };
  const sliderRef = useRef(null);
  const currentSlide = useRef(selectedIndex);

  const [state, setState] = useState({
    previousButtonDisable: selectedIndex === 0,
    nextButtonDisable: selectedIndex === dataList.length - 1,
  });

  const dataListLength = dataList.length;

  const afterSliderChage = (newIndex) => {
    currentSlide.current = newIndex;
    setState({
      ...state,
      previousButtonDisable: newIndex <= 0,
      nextButtonDisable: (newIndex + 1) >= dataListLength,
    });
  };

  const next = () => {
    if (!state.nextButtonDisable) {
      sliderRef.current.slickGoTo(currentSlide.current + 1);
      onPageChange(currentSlide.current + 1);
    }
  };

  const previous = () => {
    if (!state.previousButtonDisable) {
      sliderRef.current.slickGoTo(currentSlide.current - 1);
      onPageChange(currentSlide.current - 1);
    }
  };

  useImperativeHandle(ref, () => ({
    goToSlide: (index, dontAnimate = false) => {
      sliderRef.current.slickGoTo(index, dontAnimate);
      onPageChange(index);
    },
  }));

  const settings = {
    speed: 500,
    slidesToScroll: 1,
    slidesToShow: 1,
    lazyLoad: 'ondemand',
    className: 'card-detail-slider',
    infinite: false,
    initialSlide: selectedIndex,
    draggable: false,
    ref: sliderRef,
    arrows: false,
    afterChange: afterSliderChage,
  };

  useEffect(() => {
    if (open) {
      LiveChatService.hideLiveChatIcon();
    } else {
      LiveChatService.showLiveChatIcon();
    }
    return () => {
      LiveChatService.showLiveChatIcon();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      scroll='body'
      className={`card-detail-container ${className}`}
      {...rest}
    >
      <DialogActions className='action-area'>
        <Button onClick={onClose} color='primary' data-test='card-detail-container-close-action'>
          Close
          <CloseIcon />
        </Button>
      </DialogActions>
      {dataList ? (
        <DialogContent className='content'>
          <IconButton
            aria-label='Prevous'
            component='span'
            className={`detail-navigator-button previous ${state.previousButtonDisable ? 'disabled' : ''}`}
            onClick={previous}
            data-test='detail-navigator-button-previous'
            tabIndex={state.previousButtonDisable ? -1 : 0}
          >
            <PlayCircleFilledIcon />
          </IconButton>
          <Slider {...settings}>
            {dataList.map((data, index) => (
              createCardFunction(data, index)
            ))}
          </Slider>
          <IconButton
            aria-label='Next'
            component='span'
            className={`detail-navigator-button next ${state.nextButtonDisable ? 'disabled' : ''}`}
            data-test='detail-navigator-button-next'
            onClick={next}
            tabIndex={state.nextButtonDisable ? -1 : 0}
          >
            <PlayCircleFilledIcon />
          </IconButton>
        </DialogContent>
      ) : null}
    </Dialog>
  );
});

export default CardDetailContainer;

// hook functions for handling the container
export const useCardDetail = () => {
  const [openCardDetail, setOpenCardDetail] = useState({
    open: false,
    selectedIndex: null,
    rowIndex: null,
  });

  const handleOnViewDetailClick = (selectedIndex, rowIndex) => {
    setOpenCardDetail({
      open: true,
      selectedIndex,
      rowIndex,
    });
  };

  const handleOnCloseDetail = () => {
    setOpenCardDetail({
      open: false,
      selectedIndex: null,
      rowIndex: null,
    });
  };

  return {
    handleOnViewDetailClick,
    handleOnCloseDetail,
    openCardDetail,
    setOpenCardDetail,
  };
};
